import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import './design-system/tokens/oikra-tokens-v3.css';
import './styles/index.css';
import './styles/touch.css';
import './styles/app-light-theme.css';
import './components/ui/ui.css';
// Pin shared chart CSS in the entry chunk so async routes cannot reorder
// VisualizerChart / SkeletonLoader / ChartAttribution / InterfaceState (CI).
// app-light-theme.css is pinned right after index.css for the same reason:
// it overrides .btn-primary/.btn-secondary and other base selectors, and
// used to load from whichever async route (Layout vs LandingPage) resolved
// first, so its cascade position relative to index.css was nondeterministic
// (buttons rendering unstyled on some loads).
import './components/states/InterfaceState.css';
import './components/SkeletonLoader.css';
import './components/VisualizerChart.css';
import './components/ChartAttribution.css';
// Leaflet CSS - uncomment after running: npm install leaflet react-leaflet
// import 'leaflet/dist/leaflet.css';
import App from './App';
import { I18nextProvider } from 'react-i18next';
import i18n, { i18nReady } from './i18n';
import { queryClient } from './queryClient';

// Service worker: network-first hashed assets; reload once when a new SW claims.
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    try {
      sessionStorage.removeItem('oikra:chunk-reload');
    } catch {
      /* ignore */
    }

    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });

    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        registration.update().catch(() => {});
        // Deliberate prod-only diagnostic; the app logger suppresses log/warn outside dev.
        // eslint-disable-next-line no-console
        console.log('[Service Worker] Registered successfully:', registration.scope);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('[Service Worker] Registration failed:', error);
      });
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// i18next has no translations loaded until the active language's chunk
// arrives (see src/i18n/index.js) — wait for it so first paint isn't a
// flash of raw translation keys.
i18nReady.then(() => {
  root.render(
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <React.StrictMode>
          <App />
        </React.StrictMode>
      </QueryClientProvider>
    </I18nextProvider>
  );
});
